import { CommonModule } from "@angular/common";
import { ChangeDetectionStrategy, Component, Input } from "@angular/core";

@Component({
  selector: "app-tour-backdrop",
  standalone: true,
  imports: [CommonModule],
  template: `
    <div *ngIf="targetRect" class="tour-backdrop">
      <svg
        class="tour-backdrop-svg"
        [attr.viewBox]="viewBox"
        preserveAspectRatio="none"
      >
        <defs>
          <mask [attr.id]="maskId" maskUnits="userSpaceOnUse">
            <rect x="0" y="0" width="100%" height="100%" fill="white" />
            <rect
              [attr.x]="maskLeft"
              [attr.y]="maskTop"
              [attr.width]="maskWidth"
              [attr.height]="maskHeight"
              [attr.rx]="12"
              [attr.ry]="12"
              fill="black"
            />
          </mask>
        </defs>
        <rect
          x="0"
          y="0"
          width="100%"
          height="100%"
          fill="rgba(0, 0, 0, 0.65)"
          [attr.mask]="'url(#' + maskId + ')'"
        />
      </svg>
    </div>
    <div
      *ngIf="targetRect"
      class="tour-highlight"
      [ngStyle]="highlightStyles"
    ></div>
  `,
  styles: [
    `
      :host {
        position: fixed;
        inset: 0;
        pointer-events: none;
        z-index: 1000;
      }

      .tour-backdrop {
        position: fixed;
        inset: 0;
        pointer-events: auto;
      }

      .tour-backdrop-svg {
        width: 100%;
        height: 100%;
      }

      .tour-highlight {
        position: fixed;
        background: transparent;
        border: 2px solid var(--mat-sys-primary, #6200ee);
        border-radius: 12px;
        overflow: hidden;
        pointer-events: none;
        box-shadow: 0 0 0 0 var(--mat-sys-primary, #6200ee);
        animation: tour-highlight-glow 2s ease-in-out infinite;
      }

      @keyframes tour-highlight-glow {
        0%,
        100% {
          box-shadow: 0 0 0 0 var(--mat-sys-primary, #6200ee);
        }
        50% {
          box-shadow: 0 0 0 12px
            color-mix(
              in srgb,
              var(--mat-sys-primary, #6200ee) 15%,
              transparent 85%
            );
        }
      }

      .tour-highlight::after {
        content: "";
        position: absolute;
        inset: 0;
        border-radius: inherit;
        box-shadow: 0 0 0 1px rgba(255, 255, 255, 0.12);
      }
    `,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TourBackdropComponent {
  @Input() targetRect?: DOMRect | null;

  readonly maskId = `tour-hole-mask-${Math.random().toString(36).slice(2)}`;
  readonly highlightPadding = 8;

  get viewBox() {
    return `0 0 ${window.innerWidth} ${window.innerHeight}`;
  }

  get maskLeft() {
    return this.targetRect
      ? Math.max(0, this.targetRect.left - this.highlightPadding)
      : 0;
  }

  get maskTop() {
    return this.targetRect
      ? Math.max(0, this.targetRect.top - this.highlightPadding)
      : 0;
  }

  get maskWidth() {
    return this.targetRect
      ? this.targetRect.width + this.highlightPadding * 2
      : 0;
  }

  get maskHeight() {
    return this.targetRect
      ? this.targetRect.height + this.highlightPadding * 2
      : 0;
  }

  get highlightStyles() {
    if (!this.targetRect) {
      return {};
    }

    return {
      top: `${Math.max(0, this.targetRect.top - this.highlightPadding)}px`,
      left: `${Math.max(0, this.targetRect.left - this.highlightPadding)}px`,
      width: `${this.targetRect.width + this.highlightPadding * 2}px`,
      height: `${this.targetRect.height + this.highlightPadding * 2}px`,
    };
  }
}
